import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import 'mantine-react-table/styles.css';

import { ActionsColumn, IdColumn, NameColumn, TypeColumn } from './columns';
import {
    Box,
    Button,
    Container,
    Flex,
    Group,
    MultiSelect,
    Portal,
    Select,
    Text,
} from '@mantine/core';
import { MultiSelectCreatable, StatColumn } from '../../common';
import { getLinkGroups, getMediaGroups } from '../../../http/apiClient';
import {
    useDebouncedCallback,
    useDisclosure,
    useLocalStorage,
} from '@mantine/hooks';
import { useMemo, useState } from 'react';

import CampaignGroupStatsTimelineModal from '../../modals/timeline/CampaignGroupStatsTimelineModal';
import CreateMediaGroupModal from './modals/CreateMediaGroup';
import CreateMediaModal from './modals/CreateMedia';
import FiltersMultiSelect from '../../common/filtersMultiSelect/FiltersMultiSelect';
import GroupToolbarActions from './toolbars/GroupToolbarActions';
import { MantineReactTable } from 'mantine-react-table';
import MediaTable from './mediaTable/MediaTable';
import { StatusColumn } from '../campaigns/campaignList/columns';
import classes from './GroupTable.module.css';
import { naiveRound } from '../../../utils/text';
import { sumObjectKeys } from '../../../utils/statUtils';
import { usePaginatedQuery } from '../../../http/queries/usePaginatedQuery';

const MediaGroupsView = ({ ...props }) => {
    const [globalFilter, setGlobalFilter] = useState(null);
    const [sorting, setSorting] = useState([]);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 50,
    });

    const filterDebounce = useDebouncedCallback((query) => {
        setGlobalFilter(query);
    }, 600);

    const [
        createGroupOpened,
        { open: openCreateModal, close: closeCreateModal },
    ] = useDisclosure();

    const [modalItem, setModalItem] = useState(null);
    const [
        timelineModalOpen,
        { open: openTimelineModal, close: closeTimelineModal },
    ] = useDisclosure();

    const {
        isLoading,
        isError,
        error,
        data: paginatedData,
    } = usePaginatedQuery({
        queryFn: (queryParams) => getMediaGroups(queryParams),
        queryKey: ['media_groups'],
        refetchInterval: 15_0000,
        globalFilter,
        pagination,
        sorting,
    });

    const { data: imageGroups, pagination: dataPagination } =
        paginatedData || {};
    const columns = useMemo(
        () => [
            IdColumn(),
            NameColumn(),
            StatColumn('Sent', (x) => x.stats?.send),
            StatColumn('Clicks', (x) =>
                sumObjectKeys(x.stats, ['uniqueClick'])
            ),
            StatColumn('Previews', (x) =>
                sumObjectKeys(x.stats, ['uniquePreview'])
            ),
            StatColumn(
                'CTR',
                (x) => {
                    const uniqueClicks = sumObjectKeys(x.stats, [
                        'uniqueClick',
                    ]);
                    return naiveRound((uniqueClicks / x.stats?.send) * 100, 2);
                },
                true
            ),
            StatColumn('Conversions', (x) => x.stats?.conversion),
            StatColumn(
                'CR',
                (x) => {
                    const uniqueClicks = sumObjectKeys(x.stats, [
                        'uniqueClick',
                    ]);
                    return naiveRound(
                        (x.stats?.conversion / uniqueClicks) * 100,
                        2
                    );
                },
                true
            ),
        ],
        [imageGroups]
    );

    /*const tableData = useMemo(
        () => (isLoading ? [] : campaigns),
        [campaigns, isLoading]
    );*/

    const tableData = useMemo(
        () => (isLoading ? [] : imageGroups),
        [imageGroups, isLoading]
    );

    const [columnVisibility, setColumnVisibility] = useLocalStorage({
        key: 'image-group-table-visibility', // a unique key for local storage
        defaultValue: {}, // default to all columns shown if empty
        getInitialValueInEffect: true,
    });

    const [columnSizing, setColumnSizing] = useLocalStorage({
        key: 'image-group-table-sizing', // a unique key for local storage
        defaultValue: {}, // default to all columns shown if empty
        getInitialValueInEffect: true,
    });

    const handleVisibilityChange = (changeFunc) => {
        const changes = changeFunc();
        setColumnVisibility((prevState) => ({
            ...prevState,
            ...changes,
        }));
    };

    const handlSizeChange = (changeFunc) => {
        const changes = changeFunc();
        setColumnSizing((prevState) => ({
            ...prevState,
            ...changes,
        }));
    };

    const handleFilterChange = (queryStr) => {
        filterDebounce(queryStr);
    };

    const tableProps = {
        columns: columns,
        data: tableData,
        enableRowActions: true,
        enableColumnResizing: true,
        enableColumnActions: false,
        enableSorting: false,
        enableTopToolbar: true,
        enableGlobalFilter: true,
        enableFilters: false,
        enableStickyHeader: true,
        enableDensityToggle: false,
        positionActionsColumn: 'last',
        layoutMode: 'semantic',
        mantineTableBodyRowProps: ({
            isDetailPanel,
            row,
            staticRowIndex,
            table,
        }) => ({
            className: isDetailPanel ? classes.rowPadding : null,
            onClick: () => {
                if (!isDetailPanel) {
                    row.toggleExpanded();
                }
            },
        }),
        mantineSearchTextInputProps: {
            placeholder: 'Search Media Groups',
            miw: 200,
        },
        mantineTableContainerProps: {
            style: {
                minHeight: '80vh',
            },
        },
        positionGlobalFilter: 'left',
        positionExpandColumn: 'last',
        renderTopToolbarCustomActions: () => (
            <GroupToolbarActions
                filterOptions={[{ label: 'test', value: 'test' }]}
                openCreateModal={openCreateModal}
            />
        ),
        renderRowActions: (props) => (
            <ActionsColumn
                {...props}
                setModalItem={setModalItem}
                openTimelineModal={openTimelineModal}
            />
        ),

        mantineTableBodyRowProps: ({
            isDetailPanel,
            row,
            staticRowIndex,
            table,
        }) => ({
            className: isDetailPanel ? classes.rowPadding : null,
            onClick: (e) => {
                const INTERACTIVE_SELECTOR =
                    'a[href], button, input:not([type="hidden"]), select, textarea, label, [role="button"]';

                let target = e.target;
                if (target instanceof SVGElement) {
                    target = target.parentElement;
                }

                const interactiveElement =
                    target instanceof HTMLElement &&
                    target.closest(INTERACTIVE_SELECTOR);

                if (interactiveElement) return;

                if (!isDetailPanel) {
                    row.toggleExpanded();
                }
            },
        }),
        renderDetailPanel: ({ row, table }) => {
            return <MediaTable groupId={row.original.id} />;
        },

        manualPagination: true,
        manualSorting: true,
        onColumnVisibilityChange: handleVisibilityChange,
        onColumnSizingChange: handlSizeChange,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onGlobalFilterChange: handleFilterChange,
        rowCount: dataPagination?.totalItems || 0,
        pageCount: dataPagination?.totalPages || 1,

        initialState: {
            columnVisibility,
            pagination,
            isLoading,
            showGlobalFilter: true,
        },
        state: {
            isLoading,
            pagination,
            columnVisibility,
            columnSizing,
            showAlertBanner: isError,
            showProgressBars: isLoading,
            sorting,
            density: 'xs',
        },
    };

    return (
        <Container fluid>
            <MantineReactTable {...tableProps} />
            <CreateMediaGroupModal
                opened={createGroupOpened}
                close={closeCreateModal}
            />
            <CampaignGroupStatsTimelineModal
                resource={'media/groups'}
                resourceId={modalItem?.id}
                title={`Send Timeline - ${modalItem?.name}`}
                opened={timelineModalOpen}
                close={closeTimelineModal}
            />
        </Container>
    );
};
export default MediaGroupsView;
